import React, { ReactNode, useRef, useEffect, FC, JSX } from "react";

const events = getSupportedEvents();

const isMouseEvent = (e: unknown): e is MouseEvent =>
  typeof e === "object" && e !== null && "clientX" in e;

export const coordX = (event: MouseEvent | TouchEvent): number => {
  if ('clientX' in event) {
    return event.clientX;
  }
  return event.changedTouches && event.changedTouches[0].clientX;
};

export const coordY = (event: MouseEvent | TouchEvent): number => {
  if ('clientY' in event) {
    return event.clientY;
  }
  return event.changedTouches && event.changedTouches[0].clientY;
};

function getSupportedEvents() {
  const isClient = typeof window !== "undefined";
  const touchEnabled = isClient && "ontouchstart" in window;

  if (touchEnabled) {
    return ["touchstart", "touchmove", "touchend", "touchcancel"] as const;
  }

  return ["mousedown", "mousemove", "mouseup", "mouseleave"] as const;
}

export type TouchGestureEvent = {
  startX: number;
  startY: number;
  startT: Date;
  shiftX?: number;
  shiftY?: number;
  shiftXAbs?: number;
  shiftYAbs?: number;
  isPressed?: boolean;
  isX?: boolean;
  isY?: boolean;
  isSlide?: boolean;
  isSlideX?: boolean;
  isSlideY?: boolean;
  originalEvent: TouchEvent | MouseEvent;
};

type Props = {
  onStart?: (e: TouchGestureEvent) => void;
  onStartX?: (e: TouchGestureEvent) => void;
  onStartY?: (e: TouchGestureEvent) => void;
  onMove?: (e: TouchGestureEvent) => void;
  onMoveX?: (e: TouchGestureEvent) => void;
  onMoveY?: (e: TouchGestureEvent) => void;
  onEnd?: (e: TouchGestureEvent) => void;
  onEndX?: (e: TouchGestureEvent) => void;
  onEndY?: (e: TouchGestureEvent) => void;
  useCapture?: boolean;
  // component?: keyof JSX.IntrinsicElements;
  children: ReactNode;
} & React.HTMLAttributes<HTMLElement>;

export const Touch: FC<Props> = (props) => {
  const {
    // component = "div",
    children,
    onStart,
    onStartX,
    onStartY,
    onMove,
    onMoveX,
    onMoveY,
    onEnd,
    onEndX,
    onEndY,
    useCapture = true,
    ...nativeAttributes
  } = props;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let gesture: Omit<TouchGestureEvent, "originalEvent"> = {
      startX: 0,
      startY: 0,
      startT: new Date(0),
      isPressed: false,
    };
    let cancelClick = false;

    function onStartHandler(e: TouchEvent | MouseEvent) {
      gesture = {
        startX: coordX(e),
        startY: coordY(e),
        startT: new Date(),
        isPressed: true,
      };

      // Call the relevant callbacks from props
      const outputEvent: TouchGestureEvent = {
        ...gesture,
        originalEvent: e,
      };

      if (onStart) {
        onStart(outputEvent);
      }

      if (onStartX) {
        onStartX(outputEvent);
      }

      if (onStartY) {
        onStartY(outputEvent);
      }

      window.addEventListener(events[1], onMoveHandler, {
        capture: useCapture,
      });
      window.addEventListener(events[2], onEndHandler, { capture: useCapture });
      window.addEventListener(events[3], onEndHandler);
    }

    function onMoveHandler(e: TouchEvent | MouseEvent) {
      const { isPressed, isX, isY, startX, startY } = gesture;

      if (isPressed) {
        // Offsets relative to gesture start
        const shiftX = coordX(e) - startX;
        const shiftY = coordY(e) - startY;

        // Absolute shift values
        const shiftXAbs = Math.abs(shiftX);
        const shiftYAbs = Math.abs(shiftY);

        // Abort the gesture when a multi-touch is detected
        if (!isMouseEvent(e) && e.touches.length > 1 && onEnd) {
          return onEnd({ ...gesture, originalEvent: e });
        }

        // Determine slide direction once the offset thresholds are met
        if (!isX && !isY) {
          let willBeX = shiftXAbs >= 5 && shiftXAbs > shiftYAbs;
          let willBeY = shiftYAbs >= 5 && shiftYAbs > shiftXAbs;
          let willBeSlidedX = (willBeX && !!onMoveX) || !!onMove;
          let willBeSlidedY = (willBeY && !!onMoveY) || !!onMove;

          gesture.isY = willBeY;
          gesture.isX = willBeX;
          gesture.isSlideX = willBeSlidedX;
          gesture.isSlideY = willBeSlidedY;
          gesture.isSlide = willBeSlidedX || willBeSlidedY;
        }

        if (gesture.isSlide) {
          gesture.shiftX = shiftX;
          gesture.shiftY = shiftY;
          gesture.shiftXAbs = shiftXAbs;
          gesture.shiftYAbs = shiftYAbs;

          const outputEvent = Object.assign({}, gesture, {
            originalEvent: e,
          });

          // Call the relevant callbacks from props

          if (onMove) {
            onMove(outputEvent);
          }

          if (gesture.isSlideX && onMoveX) {
            onMoveX(outputEvent);
          }

          if (gesture.isSlideY && onMoveY) {
            onMoveY(outputEvent);
          }
        }
      }
    }

    function onEndHandler(e: TouchEvent | MouseEvent) {
      const { isPressed, isSlide, isSlideX, isSlideY } = gesture;

      if (isPressed) {
        // Call the relevant callbacks from props
        const outputEvent = Object.assign({}, gesture, {
          originalEvent: e,
        });

        if (onEnd) {
          onEnd(outputEvent);
        }

        if (isSlideY && onEndY) {
          onEndY(outputEvent);
        }

        if (isSlideX && onEndX) {
          onEndX(outputEvent);
        }
      }

      // When gesture ends on a link, set the flag to cancel navigation
      cancelClick = Boolean(
        (e.target as HTMLElement)?.tagName === "A" && isSlide
      );
      gesture = {
        startX: 0,
        startY: 0,
        startT: new Date(0),
        isPressed: false,
      };

      window.removeEventListener(events[1], onMoveHandler, {
        capture: useCapture,
      });
      window.removeEventListener(events[2], onEndHandler, {
        capture: useCapture,
      });
      window.removeEventListener(events[3], onEndHandler);
    }

    function onDragStartHandler(e: DragEvent) {
      // Prevent native drag behavior for nested links and images
      const target = e.target as HTMLElement | null;

      if (target?.tagName === "A" || target?.tagName === "IMG") {
        return e.preventDefault();
      } else return;
    }

    function onClickHandler(e: MouseEvent) {
      // Cancel navigation if a swipe finished on a nested link
      if (cancelClick) {
        cancelClick = false;

        return e.preventDefault();
      }
    }

    function onContextMenuHandler(e: MouseEvent) {
      if (onEnd) {
        onEnd({ ...gesture, originalEvent: e });
      }
    }

    if (ref.current) {
      ref.current.addEventListener(events[0], onStartHandler);
      ref.current.addEventListener("dragstart", onDragStartHandler);
      ref.current.addEventListener("click", onClickHandler);
      ref.current.addEventListener("contextmenu", onContextMenuHandler);
    }

    return () => {
      if (ref.current) {
        ref.current.removeEventListener(events[0], onStartHandler);
        ref.current.removeEventListener("dragstart", onDragStartHandler);
        ref.current.removeEventListener("click", onClickHandler);
        ref.current.removeEventListener("contextmenu", onContextMenuHandler);
      }
    };
  }, [
    onStart,
    onStartX,
    onStartY,
    onMove,
    onMoveX,
    onMoveY,
    onEnd,
    onEndX,
    onEndY,
    useCapture,
    ref,
  ]);

  // const ComponentName = component;

  return (
    <div {...nativeAttributes} ref={ref}>
      {children}
    </div>
  );
};
